import { Combobox } from '@/components/ui/combobox/combobox';
import KeyboardKeyIcon from '@/components/ui/keyboard-key-icon';
import { HOTKEYS } from '@/lib/hotkeys';
import { pdkAxios } from '@/lib/pdk-axios';
import type { Deal } from '@/models/registry';
import { dealStore } from '@/models/stores/deals';
import { useMutation } from '@tanstack/react-query';
import { noop } from 'lodash';
import { useCallback, useMemo } from 'react';
import { PRIORITY_OPTIONS_MAP } from './priority-types';

type DealPriorityComboboxProps = {
  id: Deal['id'];
  value: Deal['priority'];
  afterOnChange?: () => void;
  onSuccess?: (data: Deal, variables: { priority: Deal['priority'] }) => void;
  onError?: (error: Error, variables: { priority: Deal['priority'] }) => void;
};

export function DealPriorityCombobox({
  id,
  value,
  afterOnChange,
  onSuccess,
  onError,
}: DealPriorityComboboxProps) {
  const updatePriorityMutation = useMutation({
    onMutate: async ({ priority }) => {
      dealStore.updatePriority({ id, priority });
    },
    mutationFn: async ({ priority }: { priority: Deal['priority'] }) => {
      const response = await pdkAxios
        .post(`/deals/${id}/priority`, { priority })
        .catch((e) => {
          console.error('Error updating priority', e);
          throw e;
        });

      return response.data;
    },
    onSuccess: (data, variables) => {
      onSuccess?.(data, variables);
    },
    onError: (error, variables) => {
      console.error('Error updating priority', error, variables);
      onError?.(error, variables);
    },
    onSettled: () => {
      // DEV NOTE: invalidateQueries is cause a flicker bug
      // because the useQuery hook in useDeal re runs and gives back an old value
      // It may not even be refetching ...
      // queryClient.invalidateQueries({ queryKey: ["deal", id] });
    },
  });

  const handlePriorityChange = (newPriority: Deal['priority']) => {
    updatePriorityMutation.mutate({
      priority: newPriority,
    });
  };

  const onChange = useCallback(
    (newPriority: Deal['priority']) => {
      handlePriorityChange(newPriority);
      afterOnChange?.();
    },
    [handlePriorityChange, afterOnChange],
  );

  const currentOption = useMemo(() => {
    return PRIORITY_OPTIONS_MAP.find((option) => option.value === value);
  }, [value]);

  return (
    <Combobox
      endAdornment={<KeyboardKeyIcon>{HOTKEYS.DEAL_PRIORITY}</KeyboardKeyIcon>}
      placeholder="Set priority..."
      currentOption={currentOption}
      onChange={onChange}
      emptyMessage="No priority found"
      options={PRIORITY_OPTIONS_MAP}
      setOpen={noop}
      onKeyDown={(e) => {
        const keyPressed = e.key;
        const option = PRIORITY_OPTIONS_MAP.find(
          (option) => option.hotkey === keyPressed,
        );
        if (option) {
          onChange(option.value);
        }
      }}
    ></Combobox>
  );
}
